import React, { forwardRef, useCallback } from 'react';
import { View, StyleSheet, FlatList, Dimensions } from 'react-native';
import {
  Text,
  Card,
  useTheme,
  ActivityIndicator,
  Button,
} from 'react-native-paper';
import { useTrendingRecipes } from '@coffeeplexity/api-client';
import { RecipeCard } from '@coffeeplexity/ui';
import { Recipe, useRecipePrefsStore } from '@coffeeplexity/shared';
import { TrendingUp, ArrowRight } from 'lucide-react-native';

interface TrendingRecipesProps {
  limit?: number;
  showHeader?: boolean;
  showViewAll?: boolean;
  onRecipePress: (recipe: Recipe) => void;
  onViewAll?: () => void;
  horizontal?: boolean;
  onScroll?: (event: any) => void;
}

const { width } = Dimensions.get('window');

export const TrendingRecipes = forwardRef<FlatList, TrendingRecipesProps>(
  (
    {
      limit = 6,
      showHeader = true,
      showViewAll = true,
      onRecipePress,
      onViewAll,
      horizontal = false,
      onScroll,
    },
    ref
  ) => {
    const theme = useTheme();
    const { data, isLoading, error, refetch } = useTrendingRecipes();

    if (isLoading) {
      return (
        <View style={styles.container}>
          {showHeader && (
            <View style={styles.header}>
              <View style={styles.headerTitle}>
                <TrendingUp size={24} color={theme.colors.secondary} />
                <Text variant='headlineSmall' style={styles.title}>
                  Trending Recipes
                </Text>
              </View>
            </View>
          )}
          <View style={styles.centered}>
            <ActivityIndicator size='large' color={theme.colors.primary} />
          </View>
        </View>
      );
    }

    if (error || !data?.items) {
      return (
        <View style={styles.container}>
          {showHeader && (
            <View style={styles.header}>
              <View style={styles.headerTitle}>
                <TrendingUp size={24} color={theme.colors.secondary} />
                <Text variant='headlineSmall' style={styles.title}>
                  Trending Recipes
                </Text>
              </View>
            </View>
          )}
          <View style={styles.centered}>
            <Text
              variant='bodyMedium'
              style={[styles.errorText, { color: theme.colors.error }]}
            >
              Failed to load trending recipes
            </Text>
            <Button mode='contained' onPress={() => refetch()}>
              Try Again
            </Button>
          </View>
        </View>
      );
    }

    const recipes = data.items.filter(Boolean).slice(0, limit);

    const renderRecipe = useCallback(
      ({ item: recipe, index }: { item: Recipe; index: number }) => (
        <View
          style={[
            styles.recipeContainer,
            horizontal && styles.horizontalRecipe,
          ]}
        >
          <RecipeCard
            recipe={recipe}
            onPress={onRecipePress}
            onLike={(id: string) => {
              useRecipePrefsStore.getState().toggleLike(id);
            }}
            onBookmark={(id: string) => {
              useRecipePrefsStore.getState().toggleBookmark(id);
            }}
            liked={useRecipePrefsStore.getState().isLiked(recipe.id)}
            bookmarked={useRecipePrefsStore.getState().isBookmarked(recipe.id)}
            variant='default'
            testID={`trending-recipe-${recipe.id}`}
          />
        </View>
      ),
      [horizontal, onRecipePress]
    );

    const renderHeader = () => (
      <View style={styles.header}>
        <View style={styles.headerTitle}>
          <TrendingUp size={24} color={theme.colors.secondary} />
          <Text variant='headlineSmall' style={styles.title}>
            Trending Recipes
          </Text>
        </View>
        {showViewAll && onViewAll && (
          <Button
            mode='text'
            onPress={onViewAll}
            icon={({ size, color }) => <ArrowRight size={size} color={color} />}
            contentStyle={styles.viewAllButton}
          >
            View All
          </Button>
        )}
      </View>
    );

    const renderStats = () => (
      <Card
        style={[
          styles.statsCard,
          { backgroundColor: theme.colors.surfaceVariant },
        ]}
      >
        <Card.Content>
          <View style={styles.statsContainer}>
            <View style={styles.statItem}>
              <Text
                variant='titleMedium'
                style={{ color: theme.colors.secondary }}
              >
                {recipes
                  .reduce((sum, recipe) => sum + recipe.view_count, 0)
                  .toLocaleString()}
              </Text>
              <Text
                variant='bodySmall'
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                Total Views
              </Text>
            </View>
            <View style={styles.statItem}>
              <Text variant='titleMedium' style={{ color: theme.colors.error }}>
                {recipes
                  .reduce((sum, recipe) => sum + recipe.favorite_count, 0)
                  .toLocaleString()}
              </Text>
              <Text
                variant='bodySmall'
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                Total Likes
              </Text>
            </View>
            <View style={styles.statItem}>
              <Text
                variant='titleMedium'
                style={{ color: theme.colors.primary }}
              >
                {recipes
                  .reduce((sum, recipe) => sum + recipe.review_count, 0)
                  .toLocaleString()}
              </Text>
              <Text
                variant='bodySmall'
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                Total Reviews
              </Text>
            </View>
          </View>
        </Card.Content>
      </Card>
    );

    if (recipes.length === 0) {
      return (
        <View style={styles.container}>
          {showHeader && renderHeader()}
          <View style={styles.centered}>
            <TrendingUp size={48} color={theme.colors.onSurfaceVariant} />
            <Text
              variant='titleMedium'
              style={[
                styles.emptyTitle,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              No trending recipes
            </Text>
            <Text
              variant='bodyMedium'
              style={[
                styles.emptySubtitle,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              Check back later for trending content
            </Text>
          </View>
        </View>
      );
    }

    if (horizontal) {
      return (
        <View style={styles.container}>
          {showHeader && renderHeader()}
          <FlatList
            ref={ref}
            data={recipes}
            renderItem={renderRecipe}
            keyExtractor={item => item?.id}
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.horizontalList}
            ItemSeparatorComponent={() => <View style={styles.separator} />}
            onScroll={onScroll}
            scrollEventThrottle={16}
            removeClippedSubviews={true}
            maxToRenderPerBatch={5}
            initialNumToRender={5}
            windowSize={5}
          />
          {renderStats()}
        </View>
      );
    }

    return (
      <View style={styles.container}>
        {showHeader && renderHeader()}
        <FlatList
          ref={ref}
          data={recipes}
          renderItem={renderRecipe}
          keyExtractor={item => item?.id}
          contentContainerStyle={styles.list}
          showsVerticalScrollIndicator={false}
          ItemSeparatorComponent={() => <View style={{ height: 0 }} />}
          onScroll={onScroll}
          scrollEventThrottle={16}
          removeClippedSubviews={true}
          maxToRenderPerBatch={10}
          initialNumToRender={10}
          windowSize={10}
        />
        {renderStats()}
      </View>
    );
  }
);

const styles = StyleSheet.create({
  container: {
    marginBottom: 24,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    paddingHorizontal: 16,
  },
  headerTitle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  title: {
    fontWeight: 'bold',
  },
  viewAllButton: {
    flexDirection: 'row-reverse',
  },
  centered: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 32,
  },
  errorText: {
    marginBottom: 16,
    textAlign: 'center',
  },
  list: {
    paddingHorizontal: 16,
  },
  recipeContainer: {
    width: '100%',
    marginBottom: 0,
  },
  horizontalRecipe: {
    width: width * 0.7,
    flex: 0,
  },
  horizontalList: {
    paddingHorizontal: 16,
  },
  separator: {
    width: 12,
  },
  trendingBadge: {
    position: 'absolute',
    top: 8,
    left: 8,
    zIndex: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statsCard: {
    marginHorizontal: 16,
    marginTop: 16,
  },
  statsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  statItem: {
    alignItems: 'center',
  },
  emptyTitle: {
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtitle: {
    textAlign: 'center',
  },
});
